import { ROUTE_PATHS } from 'constants/index'
import { getTranslation } from 'helpers/translations.helpers'
import {
  NOTIFICATION_CATEGORIES,
  ORDER_SIDES,
  ORDER_STATES,
  ORDER_TYPES,
  OUTCOME_TYPES,
  PREDICTION_MARKET_MULTI_SERIES_COLORS,
  USER_ACTIVITY_TYPES
} from './predictionMarket.constants'
import { divide, getPrecision, minus, plus, times } from 'utils/math.utils'
import moment from 'moment'

export const TAKER_FEE_PREVIEW_PRICES = [10, 30, 50, 70, 90]

export function parseCryptoSubCategory (subCategory) {
  if (!subCategory || typeof subCategory !== 'string') return null
  const [asset, duration] = subCategory.split(':')
  if (!asset || !['15m', '1h', '1d'].includes(duration)) return null
  return { asset, duration }
}

export function getCryptoWindowStart (option) {
  return option?.extraData?.windowStart || null
}

function getLatestOpenCryptoWindowStart (options = []) {
  const openStarts = (options || [])
    .filter((o) => o?.state === 'open' || o?.state === 'closed')
    .map(getCryptoWindowStart)
    .filter(Boolean)
  if (!openStarts.length) return null
  return [...new Set(openStarts)].sort().at(-1) || null
}

export function getCurrentCryptoWindowStart (options = []) {
  const list = Array.isArray(options) ? options : []
  const liveStart = getLatestOpenCryptoWindowStart(list)
  if (liveStart) return liveStart

  const starts = [...new Set(list.map(getCryptoWindowStart).filter(Boolean))].sort()
  return starts.at(-1) || null
}

export function getCryptoOptionsForWindow (options = [], windowStart) {
  if (!windowStart) return []
  return (options || []).filter((o) => getCryptoWindowStart(o) === windowStart)
}

export function getCryptoHourlyRangeSortKey (option) {
  const extra = option?.extraData || {}
  const min = extra.rangeMin
  const max = extra.rangeMax

  if (max == null && min != null) return Number.POSITIVE_INFINITY
  if (min == null && max != null) return Number.NEGATIVE_INFINITY
  if (max != null) return +max
  if (min != null) return +min
  return 0
}

export function sortCryptoHourlyOptions (options = []) {
  return [...options].sort(
    (a, b) => getCryptoHourlyRangeSortKey(b) - getCryptoHourlyRangeSortKey(a)
  )
}

export function getCurrentCryptoOptions (options = [], market = null) {
  const list = Array.isArray(options) ? options : []
  if (market && market.category !== 'crypto') return list

  const hasWindows = list.some((o) => o?.extraData?.windowStart)
  if (!hasWindows) return list

  const windowStart = getCurrentCryptoWindowStart(list)
  if (!windowStart) return list

  const windowOptions = getCryptoOptionsForWindow(list, windowStart)
  const isHourly = windowOptions.some((o) => o?.extraData?.duration === '1h') ||
    market?.subCategory?.endsWith(':1h')

  return isHourly ? sortCryptoHourlyOptions(windowOptions) : windowOptions
}

export function getLiveCryptoWindowStart (options = []) {
  return getLatestOpenCryptoWindowStart(options)
}

export function getCryptoOptionsForView (options = [], market = null, viewWindowStart = null) {
  const list = Array.isArray(options) ? options : []
  if (market && market.category !== 'crypto') return list

  const hasWindows = list.some((o) => o?.extraData?.windowStart)
  if (!hasWindows) return list

  const windowStart = viewWindowStart || getCurrentCryptoWindowStart(list)
  if (!windowStart) return list

  const windowOptions = getCryptoOptionsForWindow(list, windowStart)
  const isHourly =
    windowOptions.some((o) => o?.extraData?.duration === '1h') ||
    market?.subCategory?.endsWith(':1h')

  return isHourly ? sortCryptoHourlyOptions(windowOptions) : windowOptions
}

export function getPreviousCryptoRound (options = []) {
  const list = Array.isArray(options) ? options : []
  const currentStart = getCurrentCryptoWindowStart(list)
  const starts = [...new Set(list.map(getCryptoWindowStart).filter(Boolean))].sort()
  if (!starts.length) return null

  const prevStart = currentStart
    ? starts.filter((s) => s < currentStart).at(-1)
    : starts.at(-2)

  if (!prevStart) return null

  const prevOptions = getCryptoOptionsForWindow(list, prevStart)
  const hasResult = prevOptions.some(
    (o) =>
      o?.outcome === OUTCOME_TYPES.YES ||
      o?.outcome === OUTCOME_TYPES.NO ||
      o?.state === 'resolved' ||
      o?.state === 'settled'
  )
  if (!prevOptions.length || !hasResult) return null

  return {
    windowStart: prevStart,
    windowEnd: prevOptions[0]?.extraData?.windowEnd || null,
    options: prevOptions,
    winners: prevOptions.filter((o) => (o?.extraData?.priceOutcome || o?.outcome) === OUTCOME_TYPES.YES),
    isSettled: prevOptions.every((o) => o?.state === 'settled')
  }
}

export function getCryptoMarketMeta (market, viewWindowStart = null) {
  const parsed = parseCryptoSubCategory(market?.subCategory)
  if (market?.category !== 'crypto' || !parsed) return null

  const { asset, duration } = parsed
  const allOptions = market.options || []
  const currentOptions = getCryptoOptionsForView(allOptions, market, viewWindowStart)
  const pool = currentOptions.length ? currentOptions : allOptions
  const activeOption =
    pool.find((o) => o.state === 'open') ||
    pool.find((o) => o.state === 'closed') ||
    pool[0] ||
    null
  const extra = activeOption?.extraData || {}

  return {
    asset,
    duration,
    isBinary: duration === '15m' || duration === '1d',
    isHourly: duration === '1h',
    option: activeOption,
    currentOptions: pool,
    windowStart: extra.windowStart,
    windowEnd: extra.windowEnd,
    tradingCloseAt: activeOption?.tradingCloseAt
  }
}

export function formatCryptoWindowRange (windowStart, windowEnd) {
  if (!windowStart || !windowEnd) return null
  const start = moment(windowStart)
  const end = moment(windowEnd)
  return `${start.format('D MMMM, HH:mm')}–${end.format('HH:mm')} GMT${start.format('Z')}`
}

export function formatCryptoLocalTime (date) {
  if (!date) return null
  return moment(date).format('h:mma')
}

export function formatRelativeDayLabel (date, now = moment()) {
  if (!date) return null
  const day = moment(date).startOf('day')
  const today = moment(now).startOf('day')
  const diff = day.diff(today, 'days')
  if (diff === 0) return getTranslation('cryptoRelativeToday')
  if (diff === 1) return getTranslation('cryptoRelativeTomorrow')
  if (diff === -1) return getTranslation('cryptoRelativeYesterday')
  return moment(date).format('MMM D')
}

export function formatCryptoDisplayTitle (market) {
  const crypto = getCryptoMarketMeta(market)
  if (!crypto?.asset) return market?.title

  if (crypto.duration === '15m') {
    return getTranslation('cryptoUpOrDown15min', { asset: crypto.asset })
  }

  if (!crypto.windowEnd) return market?.title

  const time = formatCryptoLocalTime(crypto.windowEnd)
  if (crypto.duration === '1h') {
    return getTranslation('cryptoPriceAtTime', { asset: crypto.asset, time })
  }
  if (crypto.duration === '1d') {
    return getTranslation('cryptoUpOrDownAtTime', {
      asset: crypto.asset,
      day: formatRelativeDayLabel(crypto.windowEnd),
      time
    })
  }
  return market?.title
}

function formatCryptoHourlyOptionTitle (option) {
  const grade = option?.extraData?.grade
  const rangeMin = option?.extraData?.rangeMin
  const rangeMax = option?.extraData?.rangeMax

  if (grade === 'B' && rangeMin != null) {
    return getTranslation('cryptoMoreThanPrice', { price: rangeMin })
  }
  if (grade === 'D' && rangeMax != null) {
    return getTranslation('cryptoLessThanPrice', { price: rangeMax })
  }
  if ((grade === 'A' || grade === 'C') && rangeMin != null && rangeMax != null) {
    return `$${rangeMin} – $${rangeMax}`
  }
  return null
}

export function formatCryptoOptionDisplayTitle (option, market = null) {
  const title = option?.title
  if (!title && !option?.extraData) return title

  const parsed = parseCryptoSubCategory(market?.subCategory)
  const duration = option?.extraData?.duration || parsed?.duration
  const windowEnd = option?.extraData?.windowEnd
  const asset = option?.extraData?.asset || parsed?.asset

  if (!duration) return title

  if (duration === '15m' && asset) {
    return getTranslation('cryptoUpOrDown15min', { asset })
  }

  if (duration === '1d' && asset && windowEnd) {
    return getTranslation('cryptoUpOrDownDay', {
      asset,
      day: formatRelativeDayLabel(windowEnd)
    })
  }

  if (duration === '1h') {
    return formatCryptoHourlyOptionTitle(option) || title
  }

  return title
}

export function formatStoredCryptoQuestionTitle (questionTitle, log = {}) {
  const subCategory = log?.subCategory
  const windowEnd = log?.windowEnd
  const parsed = parseCryptoSubCategory(subCategory)

  if (parsed?.duration === '15m') {
    return getTranslation('cryptoUpOrDown15min', { asset: parsed.asset })
  }

  if (windowEnd && parsed) {
    const time = formatCryptoLocalTime(windowEnd)
    if (parsed.duration === '1h') {
      return getTranslation('cryptoPriceAtTime', { asset: parsed.asset, time })
    }
    if (parsed.duration === '1d') {
      return getTranslation('cryptoUpOrDownAtTime', {
        asset: parsed.asset,
        day: formatRelativeDayLabel(windowEnd),
        time
      })
    }
  }

  const hourly = String(questionTitle || '').match(
    /^(.+?) price at (\d{1,2})(?::(\d{2}))?\s*(am|pm)\s*UTC$/i
  )
  if (hourly) {
    const asset = hourly[1]
    let h = Number(hourly[2])
    const mins = Number(hourly[3] || 0)
    const ap = hourly[4].toLowerCase()
    if (ap === 'pm' && h !== 12) h += 12
    if (ap === 'am' && h === 12) h = 0
    const local = moment.utc().hour(h).minute(mins).second(0).millisecond(0).local()
    return getTranslation('cryptoPriceAtTime', {
      asset,
      time: formatCryptoLocalTime(local)
    })
  }

  const daily = String(questionTitle || '').match(
    /^(.+?) Up or Down (?:today|tomorrow|yesterday|[A-Za-z]{3} \d{1,2}) at (\d{1,2})(?::(\d{2}))?\s*(am|pm)\s*UTC$/i
  )
  if (daily) {
    const asset = daily[1]
    let h = Number(daily[2])
    const mins = Number(daily[3] || 0)
    const ap = daily[4].toLowerCase()
    if (ap === 'pm' && h !== 12) h += 12
    if (ap === 'am' && h === 12) h = 0
    let local = moment.utc().hour(h).minute(mins).second(0).millisecond(0).local()
    if (!local.isAfter(moment())) {
      local = local.clone().add(1, 'day')
    }
    return getTranslation('cryptoUpOrDownAtTime', {
      asset,
      day: formatRelativeDayLabel(local),
      time: formatCryptoLocalTime(local)
    })
  }

  return questionTitle
}

export function formatCryptoRoundLabel (windowEnd) {
  if (!windowEnd) return null
  return moment(windowEnd).format('h:mma')
}

export function getProcessingFeeSummaryLabel ({ side, estimated = false } = {}) {
  if (side === ORDER_SIDES.BID) {
    return estimated
      ? getTranslation('estProcessingFeeIncluded')
      : getTranslation('processingFeeRefundable')
  }
  return estimated
    ? getTranslation('estProcessingFee')
    : getTranslation('processingFee')
}

export function getProcessingFeePlainCopy ({ orderType, side } = {}) {
  if (orderType === ORDER_TYPES.MARKET && side === ORDER_SIDES.BID) {
    return getTranslation('includedInWhatYouPay')
  }
  if (orderType === ORDER_TYPES.MARKET && side === ORDER_SIDES.ASK) {
    return getTranslation('deductedFromWhatYouReceive')
  }
  if (side === ORDER_SIDES.BID) {
    return getTranslation('heldWithYourOrder')
  }
  return getTranslation('onlyIfOrderFillsRightAway')
}

export function getMarketDetailPath ({ marketId, optionId, outcome, category } = {}) {
  const params = new URLSearchParams()
  if (marketId != null && marketId !== '') params.set('marketId', String(marketId))
  if (optionId != null && optionId !== '') params.set('optionId', String(optionId))

  if (outcome) params.set('outcome', outcome)

  const currentCategory = category || (typeof window !== 'undefined' && new URLSearchParams(window.location.search).get('category'))
  if (currentCategory) params.set('category', currentCategory)

  return `${ROUTE_PATHS.predictionMarketMarketDetail}?${params.toString()}`
}

/** Same formula as the matching engine: price¢ × (100 − price¢) × coeff / 1_000_000 per share. */
export function calcTakerFeePerShare (priceCents, coefficient) {
  const price = Number(priceCents) || 0
  const coeff = Number(coefficient) || 0
  return getPrecision(times(times(times(price, minus(100, price)), divide(coeff, 1000000)), 1))
}

export function formatTakerFeeDollars (valueInCurrency) {
  return `${getPrecision(valueInCurrency || 0).toFixed(2)}$`
}

export function formatTakerFeeCents (valueInCurrency) {
  const cents = Number(times(Number(valueInCurrency) || 0, 100)) || 0
  if (cents === 0) return '0$'
  if (cents < 0.01) return `${cents.toFixed(4)}$`
  if (cents < 1) return `${cents.toFixed(2)}$`
  return `${cents.toFixed(2)}$`
}

export function getTakerFeePreviewRows (coefficient) {
  return TAKER_FEE_PREVIEW_PRICES.map((price) => ({
    price,
    fee: calcTakerFeePerShare(price, coefficient)
  }))
}

export function getTakerFeeFormulaCopy (coefficient) {
  const coeff = Number(coefficient) || 0
  return getTranslation('feePerShareFormula', {
    coefficient: coeff
  })
}

export function formatPredictionMarketAxisTime (ms, rangeId) {
  const date = new Date(ms)
  const hoursAndMinutes = `${date.getHours()}:${date.getMinutes().toString().padStart(2, '0')}`

  if (rangeId === '1H' || rangeId === '6H') return hoursAndMinutes
  if (rangeId === '1D') return `${date.getMonth() + 1}/${date.getDate()} ${hoursAndMinutes}`

  return `${date.getMonth() + 1}/${date.getDate()}`
}

export function buildPredictionMarketMergedChartData (graphData, rangeId) {
  if (!graphData?.length) return { rows: [], series: [] }

  const normalizedGraphData = [...graphData].sort((firstOption, secondOption) => {
    const firstKey = String(firstOption?.optionId ?? firstOption?.id ?? firstOption?.title ?? '')
    const secondKey = String(secondOption?.optionId ?? secondOption?.id ?? secondOption?.title ?? '')
    return firstKey.localeCompare(secondKey)
  })

  const series = normalizedGraphData.map((seriesEntry, index) => ({
    dataKey: `price_${seriesEntry?.optionId ?? seriesEntry?.id ?? index}`,
    optionTitle: seriesEntry.optionTitle ?? seriesEntry.title ?? getTranslation('optionNumber', { number: index + 1 }),
    color: PREDICTION_MARKET_MULTI_SERIES_COLORS[index % PREDICTION_MARKET_MULTI_SERIES_COLORS.length]
  }))

  const timestampMap = new Map()
  normalizedGraphData.forEach((seriesEntry, index) => {
    const seriesDataKey = series[index].dataKey
    const dataPoints = getDataPointsForInterval(seriesEntry?.optionDataPoints, rangeId)
    for (const dataPoint of dataPoints) {
      if (dataPoint?.timestamp == null) continue
      const timestamp = +(dataPoint.timestamp)
      const price = dataPoint?.price == null ? null : +(dataPoint.price)
      if (!Number.isFinite(timestamp)) continue
      if (price != null && !Number.isFinite(price)) continue
      if (!timestampMap.has(timestamp)) timestampMap.set(timestamp, { timestamp })
      timestampMap.get(timestamp)[seriesDataKey] = price
    }
  })

  const rows = Array.from(timestampMap.values()).sort(
    (firstRow, secondRow) => firstRow.timestamp - secondRow.timestamp
  )

  return { rows: fillMergedChartSeriesRows(rows, series), series }
}

/**
 * Pin each series to the shared first/last timestamp only.
 * Do NOT forward-fill onto every other series' ticks — that creates staircase
 * (flat hold + 90° jump). Middle gaps stay null; Line connectNulls keeps curves smooth.
 */
export function fillMergedChartSeriesRows (rows, series) {
  if (!rows?.length || !series?.length) return rows ?? []

  const filled = rows.map((row) => ({ ...row }))
  const firstRow = filled[0]
  const lastRow = filled[filled.length - 1]

  for (const { dataKey } of series) {
    let firstKnown = null
    let lastKnown = null
    for (const row of filled) {
      const raw = row[dataKey]
      if (raw == null || !Number.isFinite(+raw)) continue
      const value = +raw
      if (firstKnown == null) firstKnown = value
      lastKnown = value
    }
    if (firstKnown == null) continue
    firstRow[dataKey] = firstKnown
    lastRow[dataKey] = lastKnown
  }

  return filled
}

export function getLatestSeriesValueByDataKey (rows, dataKey) {
  for (let index = rows.length - 1; index >= 0; index--) {
    const value = rows[index][dataKey]
    if (value != null && value !== undefined) return value
  }
  return null
}

/** Last known numeric value for dataKey at or before rows[rowIndex]; default when none exists */
export function getSeriesValueAtOrBeforeRow (rows, rowIndex, dataKey, defaultValue = 50) {
  if (!rows?.length || rowIndex < 0) return defaultValue
  const cappedIndex = Math.min(rowIndex, rows.length - 1)
  for (let j = cappedIndex; j >= 0; j--) {
    const value = rows[j][dataKey]
    if (value != null && value !== undefined && Number.isFinite(+(value))) return +(value)
  }
  return defaultValue
}

export function getDataPointsForInterval(dataPoints, rangeId) {
  if (!dataPoints?.length) return []

  let allData

  for (const entry of dataPoints) {
    if (entry?.interval === rangeId) {
      return entry?.data ?? []
    }

    if (entry?.interval === 'ALL') {
      allData = entry.data
    }
  }

  return allData ?? dataPoints[0]?.data ?? []
}

/**
 * Merges a live { price, timestamp } tick into every interval bucket of an option.
 *
 * Each interval carries its own spacing (the backend spaces its points equally), so we
 * derive that step from the interval's own data instead of hard-coding it. When the tick
 * lands inside the current bucket we just update that bucket's price; once a full step has
 * passed we append a new point and drop the oldest, so the window keeps sliding forward and
 * the line appears to move through time. This keeps short-lived questions (where every range
 * currently shows the same window) updating at the correct cadence per range.
 */
export function appendLivePriceToOptionDataPoints (optionDataPoints, dataPoint) {
  if (!optionDataPoints?.length || dataPoint?.price == null || dataPoint?.timestamp == null) {
    return optionDataPoints
  }

  return optionDataPoints.map(({ interval, data }) => {
    if (!data?.length) return { interval, data: [dataPoint] }

    const lastPoint = data[data.length - 1]
    const stepMs = data.length > 1
      ? lastPoint.timestamp - data[data.length - 2].timestamp
      : 0

    if (stepMs > 0 && dataPoint.timestamp - lastPoint.timestamp < stepMs) {
      const nextData = data.slice(0, -1)
      nextData.push({ timestamp: lastPoint.timestamp, price: dataPoint.price })
      return { interval, data: nextData }
    }

    const nextData = [...data, dataPoint]
    if (nextData.length > data.length) nextData.shift()
    return { interval, data: nextData }
  })
}

export function formatPredictionMarketCurrency (value) {
  return new Intl.NumberFormat('en-US', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2
  }).format(+(value || 0))
}

export function formatPredictionMarketCompactNumber (value) {
  return new Intl.NumberFormat('en-US', {
    notation: 'compact',
    maximumFractionDigits: 1
  }).format(+(value || 0))
}

export function formatPredictionMarketShortDate (value) {
  const date = new Date(+(value))
  if (Number.isNaN(date.getTime())) return ''
  return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })
}

export function getPriceFillPercent (priceValue) {
  const percent = +priceValue <= 1
    ? getPrecision(times(priceValue, 100))
    : +priceValue
  return Math.min(100, Math.max(0, percent))
}

const toLevel = (price, shares) => ({
  price: getPrecision(divide(price, 100)),
  shares: +shares,
  makerPriceCents: +price
})

const toMintLevel = (price, shares) => ({
  price: getPrecision(divide(minus(100, price), 100)),
  shares: +shares,
  makerPriceCents: +price
})

export const getOrderbookSpread = (bidsMap, asksMap) => {
  const bestBid = priceMapToLevels(bidsMap, 'desc')[0]?.price ?? null
  const bestAsk = priceMapToLevels(asksMap, 'asc')[0]?.price ?? null
  if (bestBid == null || bestAsk == null) return null
  return getPrecision(minus(bestAsk, bestBid))
}

export const getOptionChance = (orderbook, defaultChance = 50) => {
  if (!orderbook) return +defaultChance

  // A NO bid at P is an implied YES ask at (100 - P), and a NO ask at P is an
  // implied YES bid at (100 - P). Match backend getOptionPrice so thin books
  // (e.g. only a NO bid) don't collapse to a bogus 0¢ / 100¢ display.
  const yesBestBid = priceMapToLevels(orderbook.yes?.bid, 'desc')[0]?.price ?? null
  const yesBestAsk = priceMapToLevels(orderbook.yes?.ask, 'asc')[0]?.price ?? null
  const noBestBid = priceMapToLevels(orderbook.no?.bid, 'desc')[0]?.price ?? null
  const noBestAsk = priceMapToLevels(orderbook.no?.ask, 'asc')[0]?.price ?? null

  const yesBidFromNoAsk = noBestAsk != null ? +minus(100, noBestAsk) : null
  const yesAskFromNoBid = noBestBid != null ? +minus(100, noBestBid) : null

  const yesBid = yesBidFromNoAsk != null && yesBestBid != null
    ? Math.max(yesBestBid, yesBidFromNoAsk)
    : (yesBestBid ?? yesBidFromNoAsk ?? null)

  const yesAsk = yesAskFromNoBid != null && yesBestAsk != null
    ? Math.min(yesBestAsk, yesAskFromNoBid)
    : (yesBestAsk ?? yesAskFromNoBid ?? null)

  if (yesBid != null && yesAsk != null) {
    return getPrecision(divide(plus(yesBid, yesAsk), 2))
  }

  return yesBid ?? yesAsk ?? (+defaultChance || 50)
}



export const buildMarketOrderLiquidity = (orderbook) => {
  if (!orderbook) return null

  const buySide = (asksMap, oppositeBidsMap) =>
    [
      ...priceMapToLevels(asksMap, 'asc').map(({ price, shares }) => toLevel(price, shares)),
      ...priceMapToLevels(oppositeBidsMap, 'desc').map(({ price, shares }) => toMintLevel(price, shares))
    ].sort((a, b) => getPrecision(minus(a.price, b.price)))

  return {
    buy: {
      yes: buySide(orderbook.yes.ask, orderbook.no.bid),
      no: buySide(orderbook.no.ask, orderbook.yes.bid)
    },
    sell: {
      yes: priceMapToLevels(orderbook.yes.bid, 'desc').map(({ price, shares }) => toLevel(price, shares)),
      no: priceMapToLevels(orderbook.no.bid, 'desc').map(({ price, shares }) => toLevel(price, shares))
    }
  }
}

/** Estimate prepaid taker fee for a market buy by walking current book levels. */
export const estimateMarketBuyFee = (levels, cash, coefficient) => {
  let remainingCash = getPrecision(cash || 0)
  let fee = 0
  const coeff = Number(coefficient) || 0

  for (const level of levels || []) {
    if (remainingCash <= 0) break
    const priceDollars = +level.price
    const available = +level.shares
    const fillShares = Math.min(available, divide(remainingCash, priceDollars))
    if (fillShares <= 0) break
    const makerPriceCents = level.makerPriceCents ?? times(priceDollars, 100)
    fee = plus(
      fee,
      times(times(times(makerPriceCents, minus(100, makerPriceCents)), divide(coeff, 1000000)), fillShares)
    )
    remainingCash = minus(remainingCash, times(fillShares, priceDollars))
  }

  return getPrecision(fee)
}

/** Estimate taker fee for a market sell by walking current bids. */
export const estimateMarketSellFee = (levels, sharesToSell, coefficient) => {
  let remainingShares = getPrecision(sharesToSell || 0)
  let fee = 0
  const coeff = Number(coefficient) || 0

  for (const level of levels || []) {
    if (remainingShares <= 0) break
    const fillShares = Math.min(+level.shares, remainingShares)
    if (fillShares <= 0) break
    const makerPriceCents = level.makerPriceCents ?? times(+level.price, 100)
    fee = plus(
      fee,
      times(times(times(makerPriceCents, minus(100, makerPriceCents)), divide(coeff, 1000000)), fillShares)
    )
    remainingShares = minus(remainingShares, fillShares)
  }

  return getPrecision(fee)
}

const sumMyOrdersAtPrice = (myOrders, outcome, side, priceCents) => {
  if (!myOrders?.length) return 0
  return (myOrders || [])
    .filter((o) => o.outcome === outcome && o.side === side && +o.price === +priceCents)
    .reduce((sum, o) => plus(sum, +(o.remainingShares ?? o.shares ?? 0)), 0)
}

const excludeOwnSharesFromBuyLevel = (level, myOrders, outcome) => {
  let qty = +level.shares || 0
  if (!myOrders?.length || !outcome) return qty

  const cents = +level.makerPriceCents || times(+level.price, 100)
  const myAsk = sumMyOrdersAtPrice(myOrders, outcome, ORDER_SIDES.ASK, cents)
  const opposite = outcome === OUTCOME_TYPES.YES ? OUTCOME_TYPES.NO : OUTCOME_TYPES.YES
  const myMintBid = sumMyOrdersAtPrice(myOrders, opposite, ORDER_SIDES.BID, cents)
  return Math.max(0, +minus(qty, plus(myAsk, myMintBid)))
}

/** Dollar depth (share cost + estimated fee) to take the displayed buy book, excluding own resting orders. */
export const getMarketBuyAvailableAmount = (levels, coefficient = 0, { myOrders, outcome } = {}) => {
  const coeff = Number(coefficient) || 0
  let shareCost = 0
  let fee = 0

  for (const level of levels || []) {
    const qty = excludeOwnSharesFromBuyLevel(level, myOrders, outcome)
    if (qty <= 0) continue
    shareCost = plus(shareCost, times(level.price, qty))
    const makerPriceCents = level.makerPriceCents ?? times(+level.price, 100)
    fee = plus(
      fee,
      times(times(times(makerPriceCents, minus(100, makerPriceCents)), divide(coeff, 1000000)), qty)
    )
  }

  return getPrecision(plus(shareCost, fee))
}

/** Share depth on displayed bids a market sell can hit, excluding own resting bids. */
export const getMarketSellAvailableShares = (levels, { myOrders, outcome } = {}) => {
  let shares = 0
  for (const level of levels || []) {
    let qty = +level.shares || 0
    if (myOrders?.length && outcome) {
      const cents = +level.makerPriceCents || times(+level.price, 100)
      const myBid = sumMyOrdersAtPrice(myOrders, outcome, ORDER_SIDES.BID, cents)
      qty = Math.max(0, +minus(qty, myBid))
    }
    shares = plus(shares, qty)
  }
  return getPrecision(shares)
}

/** Price map from API: { [priceCents]: shares }. Skips placeholder key 0. */
export const priceMapToLevels = (priceMap, sortDir = 'asc') => {
  if (!priceMap || typeof priceMap !== 'object') return []

  const levels = Object.entries(priceMap)
    .filter(([price, shares]) => +price > 0 && +shares > 0)
    .map(([price, shares]) => ({ price: +price, shares: getPrecision(shares) }))

  levels.sort((a, b) => (sortDir === 'desc' ? b.price - a.price : a.price - b.price))
  return levels
}

export const withCumulativeTotal = (rows) => {
  let cumulative = 0

  return rows.map((row) => {
    const levelCost = getPrecision(times(divide(row.price, 100), row.shares))
    cumulative = getPrecision(plus(cumulative, levelCost))
    return { ...row, total: cumulative }
  })
}

export const buildOrderbookDisplaySide = (side) => {
  const bid = side?.bid
  const ask = side?.ask
  const bids = priceMapToLevels(bid, 'desc')
  const asks = priceMapToLevels(ask, 'asc')

  return {
    asks: withCumulativeTotal(asks),
    bids: withCumulativeTotal(bids),
    spread: getOrderbookSpread(bid, ask)
  }
}

export const expandOrdersWithMyOrders = (outcome, side, book, myOrders = []) => {
  return book.flatMap((order) => {
    const myOrder = myOrders.find((o) => o.outcome === outcome && o.side === side && +o.price === +order.price)

    if (!myOrder) return [{ ...order, isUserOrder: false }]

    const myShares = getPrecision(myOrder.shares)
    const remainingShares = getPrecision(minus(order.shares, myShares))
    const rows = [{ ...order, shares: myShares, isUserOrder: true }]

    if (remainingShares > 0) {
      rows.push({ ...order, shares: remainingShares, isUserOrder: false })
    }

    return rows
  })
}

export const walkBuyLiquidity = (orders, cash) => {
  let remainingCash = cash
  let acquiredShares = 0

  for (const level of orders) {
    const maxCost = getPrecision(times(level.price, level.shares))

    if (getPrecision(remainingCash) <= maxCost) {
      acquiredShares = getPrecision(plus(acquiredShares, divide(remainingCash, level.price)))
      break
    }

    acquiredShares = getPrecision(plus(acquiredShares, level.shares))
    remainingCash = getPrecision(minus(remainingCash, maxCost))
  }

  return acquiredShares
}

export const walkSellLiquidity = (orders, sharesToSell) => {
  let remainingShares = sharesToSell
  let totalProceeds = 0
  let soldShares = 0

  for (const level of orders) {
    if (remainingShares <= 0) break

    const traded = Math.min(remainingShares, level.shares)
    totalProceeds = getPrecision(plus(totalProceeds, times(traded, level.price)))
    soldShares = getPrecision(plus(soldShares, traded))
    remainingShares = getPrecision(minus(remainingShares, traded))
  }

  return {
    proceeds: totalProceeds,
    avgPrice: soldShares > 0 ? getPrecision(divide(totalProceeds, soldShares)) : 0
  }
}

/** Page numbers with ellipses for large totals (same pattern as common table pagination). */
export function getPaginationItems (currentPage, totalPages, siblingCount = 1) {
  if (totalPages <= 7) {
    return Array.from({ length: totalPages }, (_, idx) => idx + 1)
  }

  const startPage = Math.max(2, currentPage - siblingCount)
  const endPage = Math.min(totalPages - 1, currentPage + siblingCount)
  const pages = [1]

  if (startPage > 2) {
    pages.push('ellipsis-left')
  }

  for (let pageNumber = startPage; pageNumber <= endPage; pageNumber += 1) {
    pages.push(pageNumber)
  }

  if (endPage < totalPages - 1) {
    pages.push('ellipsis-right')
  }

  pages.push(totalPages)
  return pages
}

/**
 * Returns the relevant size/fill columns for a portfolio order row.
 * - Limit orders (any side): remainingShares + shares
 * - Market ask: remainingShares + shares
 * - Market bid: remainingAmount + amount
 */
export function getPortfolioOrderSliceFields (order) {
  if (!order) return {}

  if (order.orderType === ORDER_TYPES.MARKET && order.side === ORDER_SIDES.BID) {
    return {
      remainingAmount: `$${(+(order.remainingAmount ?? 0)).toFixed(2)}`,
      amount: `$${(+(order.amount ?? 0)).toFixed(2)}`,
    }
  }

  return {
    remainingShares: (+(order.remainingShares ?? 0)).toFixed(2),
    shares: (+(order.shares ?? 0)).toFixed(2),
    ...(order.orderType === ORDER_TYPES.MARKET ? {} : { price: order.price })
  }
}

export const mapOptionOrderRow = (order) => ({
  side: order.side,
  outcome: order.outcome,
  state: order.state,
  orderType: order.orderType,
  ...getPortfolioOrderSliceFields(order),
  id: order.id,
  questionId: order.questionId,
  optionId: order.optionId
})

const parseOrderAmount = (value) => parseFloat(String(value ?? '').replace(/[$,]/g, '')) || 0

export const orderMatchesFilters = (order, { state, orderType, side } = {}) => {
  if (orderType && order.orderType !== orderType) return false
  if (side && order.side !== side) return false
  if (!state) return true
  if (state === ORDER_STATES.OPEN) {
    return order.state === ORDER_STATES.OPEN || order.state === ORDER_STATES.PARTIALLY_FILLED
  }
  return order.state === state
}

export const updateMyOptionOrders = (myOptionOrders, activity, chosenOptionId, myUserId) => {
  if (!chosenOptionId || +activity.optionId !== +chosenOptionId) {
    return myOptionOrders
  }

  const { type, log } = activity
  const order = log?.order
  const trade = log?.trade
  const filters = {
    state: myOptionOrders.state,
    orderType: myOptionOrders.orderType,
    side: myOptionOrders.side
  }
  let rows = [...(myOptionOrders.rows ?? [])]
  let count = myOptionOrders.count ?? 0

  if (type === USER_ACTIVITY_TYPES.ORDER_PLACED && order) {
    if (myUserId != null && +activity.userId !== +myUserId) return myOptionOrders
    if (!orderMatchesFilters(order, filters)) return myOptionOrders

    const row = mapOptionOrderRow({
      ...order,
      questionId: activity.questionId ?? order.questionId,
      optionId: activity.optionId ?? order.optionId
    })
    const index = rows.findIndex((item) => +item.id === +row.id)

    if (index === -1) {
      rows = [row, ...rows]
      count += 1
    } else {
      rows[index] = row
    }
  } else if (type === USER_ACTIVITY_TYPES.ORDER_RESTED && order) {
    if (myUserId != null && +activity.userId !== +myUserId) return myOptionOrders

    const index = rows.findIndex((item) => +item.id === +order.id)
    const row = mapOptionOrderRow({
      ...order,
      questionId: activity.questionId ?? order.questionId,
      optionId: activity.optionId ?? order.optionId
    })
    const matches = orderMatchesFilters(row, filters)

    if (!matches) {
      if (index === -1) return myOptionOrders
      rows = rows.filter((item) => +item.id !== +order.id)
      count = Math.max(0, count - 1)
      return { ...myOptionOrders, rows, count }
    }

    if (index === -1) {
      rows = [row, ...rows]
      count += 1
    } else {
      rows[index] = row
    }
  } else if (type === USER_ACTIVITY_TYPES.ORDER_CLOSED && order) {
    if (myUserId != null && +activity.userId !== +myUserId) return myOptionOrders

    const index = rows.findIndex((item) => +item.id === +order.id)
    const matches = orderMatchesFilters(order, filters)

    if (!matches) {
      if (index === -1) return myOptionOrders
      rows = rows.filter((item) => +item.id !== +order.id)
      count = Math.max(0, count - 1)
      return { ...myOptionOrders, rows, count }
    }

    const row = mapOptionOrderRow({
      ...order,
      questionId: activity.questionId ?? order.questionId,
      optionId: activity.optionId ?? order.optionId
    })

    if (index === -1) {
      rows = [row, ...rows]
      count += 1
    } else {
      rows[index] = row
    }
  } else if (
    (type === USER_ACTIVITY_TYPES.BOUGHT_SHARES || type === USER_ACTIVITY_TYPES.SOLD_SHARES) &&
    trade &&
    myUserId != null
  ) {
    let quantityLeft = +trade.quantity

    rows = rows.flatMap((row) => {
      if (quantityLeft <= 0) return [row]

      const matchesBid = row.side === ORDER_SIDES.BID &&
        +trade.buyerId === +myUserId &&
        +trade.buyOrderId === +row.id
      const matchesAsk = row.side === ORDER_SIDES.ASK &&
        +trade.sellerId === +myUserId &&
        +trade.sellOrderId === +row.id

      if (row.outcome !== trade.outcome || (!matchesBid && !matchesAsk)) return [row]

      const remainingShares = parseOrderAmount(row.remainingShares)
      const filled = Math.min(remainingShares, quantityLeft)
      quantityLeft -= filled
      const newRemaining = minus(remainingShares, filled)

      if (newRemaining <= 0 && filters.state === ORDER_STATES.OPEN) {
        count = Math.max(0, count - 1)
        return []
      }

      return [mapOptionOrderRow({
        ...row,
        orderType: row.orderType ?? (row.remainingAmount != null ? ORDER_TYPES.MARKET : ORDER_TYPES.LIMIT),
        remainingShares: newRemaining,
        shares: parseOrderAmount(row.shares)
      })]
    })
  }

  return { ...myOptionOrders, rows, count }
}

export function getOrderDescriptiveDetails ({
  orderType,
  side,
  outcome,
  amount = 0,
  shares = 0,
  price = 0,
  optionTitle = '',
  takerFeeCoefficient = 0,
  makerRebatePercent = 0,
  postOnly = false,
  processingFee = 0,
  outcomeLabel: outcomeLabelOverride,
  yesLabel = getTranslation('yes'),
  noLabel = getTranslation('no'),
}) {
  const outcomeLabel = outcomeLabelOverride || getTranslation(outcome)
  const sideLabel = getTranslation(side === ORDER_SIDES.BID ? 'buy' : 'sell')
  const confirmLabel = `${sideLabel} ${outcomeLabel}`
  const marketTitle = optionTitle || getTranslation('thisMarket')
  const rebatePercent = Number(makerRebatePercent) || 0
  const shareCount = Number(shares) || 0
  const limitPrice = Number(price) || 0
  const fee = Number(processingFee) || 0
  const money = (value) => `$${Number(value || 0).toFixed(2)}`
  const feeMoney = (value) => {
    const n = Number(value) || 0
    if (n === 0) return '$0.00'
    return n < 0.01 ? `$${n.toFixed(8)}` : `$${n.toFixed(2)}`
  }

  const feeLines = [getProcessingFeePlainCopy({ orderType, side })]
  const estimatedValue = (value) => getTranslation('estValue', { value })

  if (rebatePercent > 0) {
    feeLines.push(getTranslation('makerFeeRebateExplanation', {
      rebatePercent
    }))
  }

  if (orderType === ORDER_TYPES.LIMIT && side === ORDER_SIDES.BID && postOnly) {
    feeLines.push(getTranslation('postOnlyOrderExplanation'))
  }

  if (orderType === ORDER_TYPES.MARKET && side === ORDER_SIDES.BID) {
    return {
      title: getTranslation('buyOutcome', { outcome: outcomeLabel }),
      message: getTranslation('marketBuyOn', { marketTitle }),
      confirmLabel,
      summaryRows: [
        { label: getTranslation('youPay'), value: money(amount) },
        { label: getTranslation('processingFees'), value: estimatedValue(feeMoney(fee)) },
        { label: getTranslation('youWillReceive'), value: getTranslation('outcomeSharesAtMarket', { outcome: outcomeLabel }) },
      ],
      feeLines,
    }
  }

  if (orderType === ORDER_TYPES.MARKET && side === ORDER_SIDES.ASK) {
    return {
      title: getTranslation('sellOutcome', { outcome: outcomeLabel }),
      message: getTranslation('marketSellOn', { marketTitle }),
      confirmLabel,
      summaryRows: [
        { label: getTranslation('youSell'), value: getTranslation('sharesOutcome', { shares: shareCount || 0, outcome: outcomeLabel }) },
        { label: getTranslation('processingFees'), value: estimatedValue(feeMoney(fee)) },
        { label: getTranslation('youWillReceive'), value: getTranslation('estProceedsAtMarket') },
      ],
      feeLines,
    }
  }

  if (orderType === ORDER_TYPES.LIMIT && side === ORDER_SIDES.BID) {
    return {
      title: postOnly ? getTranslation('postOnlyLimitBuy') : getTranslation('limitBuy'),
      message: getTranslation('sharesOutcomeAtPrice', {
        shares: shareCount || 0,
        outcome: outcomeLabel,
        price: limitPrice || 0,
        marketTitle
      }),
      confirmLabel,
      summaryRows: [
        { label: getTranslation('youPay'), value: money(amount) },
        { label: getTranslation('processingFees'), value: feeMoney(fee) },
        { label: getTranslation('youWillReceive'), value: getTranslation('sharesOutcome', { shares: shareCount || 0, outcome: outcomeLabel }) },
      ],
      feeLines,
    }
  }

  if (orderType === ORDER_TYPES.LIMIT && side === ORDER_SIDES.ASK) {
    const gross = +(shareCount * limitPrice / 100).toFixed(2)
    const net = Math.max(0, +(gross - fee).toFixed(8))
    return {
      title: getTranslation('limitSell'),
      message: getTranslation('sharesOutcomeAtPrice', {
        shares: shareCount || 0,
        outcome: outcomeLabel,
        price: limitPrice || 0,
        marketTitle
      }),
      confirmLabel,
      summaryRows: [
        { label: getTranslation('youSell'), value: getTranslation('sharesOutcome', { shares: shareCount || 0, outcome: outcomeLabel }) },
        { label: getTranslation('processingFees'), value: feeMoney(fee) },
        { label: getTranslation('youWillReceive'), value: estimatedValue(feeMoney(net)) },
      ],
      feeLines,
    }
  }

  if (orderType === ORDER_TYPES.SPLIT) {
    return {
      title: getTranslation('splitCollateral'),
      message: getTranslation('equalYesAndNoShares', { marketTitle, yes: yesLabel, no: noLabel }),
      confirmLabel: getTranslation('split'),
      summaryRows: [
        { label: getTranslation('youPay'), value: money(amount) },
        { label: getTranslation('processingFees'), value: '$0.00' },
        {
          label: getTranslation('youWillReceive'),
          value: getTranslation('amountYesAndNo', {
            amount: Number(amount || 0).toFixed(2),
            yes: yesLabel,
            no: noLabel,
          })
        },
      ],
      feeLines: [getTranslation('splitHasNoProcessingFee')],
    }
  }

  return {
    title: getTranslation('mergeShares'),
    message: getTranslation('backToCollateral', { marketTitle }),
    confirmLabel: getTranslation('merge'),
    summaryRows: [
      {
        label: getTranslation('youMerge'),
        value: getTranslation('sharesYesAndNo', {
          shares: shareCount || 0,
          yes: yesLabel,
          no: noLabel,
        })
      },
      { label: getTranslation('processingFees'), value: '$0.00' },
      { label: getTranslation('youWillReceive'), value: money(shareCount) },
    ],
    feeLines: [getTranslation('mergeHasNoProcessingFee')],
  }
}

export function getFundsError ({
  orderType,
  side,
  outcome,
  amount = 0,
  amountStr = '',
  sharesLimitStr = '',
  sharesMarketStr = '',
  walletBalance = 0,
  yesLabel = getTranslation('yes'),
  noLabel = getTranslation('no'),
  yesShares = 0,
  noShares = 0,
  lockedYesShares = 0,
  lockedNoShares = 0,
} = {}) {
  const balance = getPrecision(walletBalance ?? 0)
  const yesAvailable = getPrecision(minus(yesShares || 0, lockedYesShares || 0))
  const noAvailable = getPrecision(minus(noShares || 0, lockedNoShares || 0))
  const sellableShares = outcome === OUTCOME_TYPES.YES ? yesAvailable : noAvailable
  const mergeableShares = Math.min(yesAvailable, noAvailable)

  if (orderType === ORDER_TYPES.SPLIT) {
    const pay = getPrecision(amountStr || 0)
    if (pay > 0 && pay > balance) return getTranslation('notEnoughMoneyInWallet')
    return null
  }

  if (orderType === ORDER_TYPES.MERGE) {
    const shares = getPrecision(sharesLimitStr || 0)
    if (shares > 0 && shares > mergeableShares) return getTranslation('notEnoughYesAndNoSharesToMerge', { yes: yesLabel, no: noLabel })
    return null
  }

  if (side === ORDER_SIDES.BID) {
    const pay = getPrecision(amount || 0)
    if (pay > 0 && pay > balance) return getTranslation('notEnoughMoneyInWallet')
    return null
  }

  const sharesToSell = getPrecision(
    orderType === ORDER_TYPES.MARKET ? (sharesMarketStr || 0) : (sharesLimitStr || 0)
  )
  if (sharesToSell > 0 && sharesToSell > sellableShares) return getTranslation('notEnoughSharesToSell')
  return null
}

export const mapHistoryLogRow = (row) => {
  const { log } = row
  let { order = null, trade = null } = log

  const fmt = (value) =>
    value == null ? value : Number(value).toFixed(2)

  const rawQuestionTitle = log?.questionTitle
  let result = {
    thumbnailUrl: log?.thumbnailUrl,
    questionTitle: formatStoredCryptoQuestionTitle(rawQuestionTitle, log),
    optionTitle: log?.optionTitle
  }

  switch (log.type) {
    case 'order_placed':
      switch (order?.orderType) {
        case ORDER_TYPES.LIMIT:
          result = {
            ...result,
            description:
              order?.side === ORDER_SIDES.BID
                ? getTranslation('placedBuyLimitOrder', {
                    shares: fmt(order?.shares),
                    price: fmt(order?.price)
                  })
                : getTranslation('placedSellLimitOrder', {
                    shares: fmt(order?.shares),
                    price: fmt(order?.price)
                  })
          }
          break

        case ORDER_TYPES.MARKET:
          result = {
            ...result,
            description:
              order?.side === ORDER_SIDES.BID
                ? getTranslation('placedBuyMarketOrder', {
                    amount: fmt(order?.amount)
                  })
                : getTranslation('placedSellMarketOrder', {
                    shares: fmt(order?.shares)
                  })
          }
          break

        default:
          result = {
            ...result,
            description: getTranslation('unknownOrderType', { orderType: order?.type })
          }
      }
      break

    case 'merge':
      result = {
        ...result,
        description: getTranslation('mergedYesAndNoShares', {
          quantity: fmt(trade?.quantity),
          amount: fmt(trade?.quantity)
        })
      }
      break

    case 'split':
      result = {
        ...result,
        description: getTranslation('splitAmountIntoYesAndNoShares', {
          amount: fmt(order?.amount),
          yesShares: fmt(order?.shares / 2),
          noShares: fmt(order?.shares / 2)
        })
      }
      break

    case 'order_closed':
      switch (order?.orderType) {
        case ORDER_TYPES.LIMIT:
          result = {
            ...result,
            description:
              order?.side === ORDER_SIDES.BID
                ? getTranslation('closedBuyLimitOrder', {
                    remainingShares: fmt(order?.remainingShares),
                    price: fmt(order?.price),
                    shares: fmt(order?.shares)
                  })
                : getTranslation('closedSellLimitOrder', {
                    remainingShares: fmt(order?.remainingShares),
                    price: fmt(order?.price),
                    shares: fmt(order?.shares)
                  })
          }
          break

        case ORDER_TYPES.MARKET:
          result = {
            ...result,
            description:
              order?.side === ORDER_SIDES.BID
                ? getTranslation('closedBuyMarketOrder', {
                    remainingAmount: fmt(order?.remainingAmount),
                    amount: fmt(order?.amount)
                  })
                : getTranslation('closedSellMarketOrder', {
                    remainingShares: fmt(order?.remainingShares),
                    shares: fmt(order?.shares)
                  })
          }
          break

        default:
          return {
            ...result,
            description: getTranslation('unknownOrderType', { orderType: order?.orderType })
          }
      }
      break

    case 'bought_shares':
      result = {
        ...result,
        description: getTranslation('boughtSharesFromSeller', {
          quantity: fmt(trade?.quantity),
          price: fmt(trade?.price),
          sellerName: trade?.sellerName || getTranslation('system')
        })
      }
      break

    case 'sold_shares':
      result = {
        ...result,
        description: getTranslation('soldSharesToBuyer', {
          quantity: fmt(trade?.quantity),
          price: fmt(trade?.price),
          buyerName: trade?.buyerName || getTranslation('system')
        })
      }
      break

    case 'position_resolved': {
      const position = log?.position
      result = {
        ...result,
        description: getTranslation('marketResolvedWithOutcome', {
          outcome: getTranslation(position?.outcome),
          payout: fmt(position?.payout),
          winningShares: fmt(position?.winningShares)
        })
      }
      break
    }
  }

  return result
}

export const mapActivityLogRow = (row) => {
  return mapHistoryLogRow(row)
}

export const mapMarketActivityLogRow = (row) => {
  const { log = {}, type } = row
  const { trade = null } = log

  const fmt = (value) =>
    value == null ? value : Number(value).toFixed(2)

  const buyerName = trade?.buyerName || getTranslation('system')
  const sellerName = trade?.sellerName || getTranslation('system')
  const isSystemMinted = !trade?.sellerId

  const result = {
    // thumbnailUrl: log?.thumbnailUrl,
    // questionTitle: log?.questionTitle,
    // optionTitle: log?.optionTitle,
    outcome: trade?.outcome ? getTranslation(trade.outcome) : '-',
    // type
  }

  if (type === 'bought_shares') {
    return {
      ...result,
      description: isSystemMinted
        ? getTranslation('buyerReceivedSharesSystemMinted', {
            buyerName,
            quantity: fmt(trade?.quantity),
            price: fmt(trade?.price)
          })
        : getTranslation('buyerBoughtSharesFromSeller', {
            buyerName,
            quantity: fmt(trade?.quantity),
            price: fmt(trade?.price),
            sellerName
          })
    }
  }

  if (type === 'sold_shares') {
    return {
      ...result,
      description: !trade?.buyerId
        ? getTranslation('sellerSoldSharesToSystem', {
            sellerName,
            quantity: fmt(trade?.quantity),
            price: fmt(trade?.price)
          })
        : getTranslation('sellerSoldSharesToBuyer', {
            sellerName,
            quantity: fmt(trade?.quantity),
            price: fmt(trade?.price),
            buyerName
          })
    }
  }

  return {
    ...result,
    description: ''
  }
}


const addSharesAtPrice = (sideMap, price, shares) => {
  if (!sideMap) return

  const current = sideMap[price] ?? 0
  sideMap[price] = plus(current, shares)
}

const removeSharesAtPrice = (sideMap, price, sharesToRemove) => {
  if (!sideMap) return

  const available = sideMap[price] ?? 0
  if (+minus(available, sharesToRemove) > 0) {
    sideMap[price] = minus(available, sharesToRemove)
  } else {
    delete sideMap[price]
  }
}

export const updateOrderbook = (orderbook, marketActivity) => {
  const { type, log } = marketActivity
  const updatedOrderbook = JSON.parse(JSON.stringify(orderbook))

  switch (type) {
    case USER_ACTIVITY_TYPES.ORDER_RESTED: {
      if (log.order?.orderType && log.order.orderType !== ORDER_TYPES.LIMIT) break
      const { outcome, side, remainingShares, price } = log.order
      const restedShares = +(remainingShares ?? 0)
      if (restedShares > 0) {
        addSharesAtPrice(updatedOrderbook[outcome]?.[side], price, restedShares)
      }
      break
    }
    case USER_ACTIVITY_TYPES.BOUGHT_SHARES: {
      // Paired SOLD_SHARES uses the same trade — apply once here on the maker side only.
      // Trade rows have no `side`; seller-maker sat on asks, otherwise bids.
      const trade = log?.trade
      if (!trade) break
      const { outcome, price, quantity } = trade
      const makerSide = trade.sellerId != null && +trade.makerId === +trade.sellerId
        ? ORDER_SIDES.ASK
        : ORDER_SIDES.BID
      removeSharesAtPrice(updatedOrderbook[outcome]?.[makerSide], price, quantity)
      break
    }
    case USER_ACTIVITY_TYPES.SOLD_SHARES:
      break
    case USER_ACTIVITY_TYPES.ORDER_CLOSED: {
      if (log.order?.orderType && log.order.orderType !== ORDER_TYPES.LIMIT) break
      if (!log.order?.isRested) break
      const { outcome, side, remainingShares, price } = log.order
      removeSharesAtPrice(updatedOrderbook[outcome]?.[side], price, remainingShares)
      break
    }
  }
  return updatedOrderbook
}

export const updateMyActiveMarketOrder = (activeMarketOrder, userActivity) => {
  const { type, log } = userActivity
  
  if (type === USER_ACTIVITY_TYPES.ORDER_PLACED && log?.order?.orderType === ORDER_TYPES.MARKET) {
    const updatedOrder = log?.order ?? null
    updatedOrder.questionTitle = log?.questionTitle
    updatedOrder.optionTitle = log?.optionTitle

    return updatedOrder
  } else if (type === USER_ACTIVITY_TYPES.ORDER_CLOSED && log?.order?.orderType === ORDER_TYPES.MARKET) {
    return null
  }

  return activeMarketOrder
}

export const updateMyLimitOrders = (myLimitOrders, userActivity, myUserId) => {
  const { type, log, userId: activityUserId } = userActivity
  const trade = log?.trade

  let updatedMyLimitOrders = myLimitOrders

  // Only BOUGHT_SHARES: market feed also emits SOLD_SHARES for the same trade;
  // processing both would double-reduce. Match by order id — fill price is maker's.
  if (type === USER_ACTIVITY_TYPES.BOUGHT_SHARES && trade && myUserId != null) {
    const tradeOptionId = trade.optionId ?? userActivity.optionId ?? log?.optionId
    updatedMyLimitOrders = myLimitOrders.flatMap(order => {
      if (+order.optionId !== +tradeOptionId || order.outcome !== trade.outcome) {
        return [order]
      }

      const isMyBidFill = order.side === ORDER_SIDES.BID &&
        +trade.buyerId === +myUserId &&
        +trade.buyOrderId === +order.id
      const isMyAskFill = order.side === ORDER_SIDES.ASK &&
        +trade.sellerId === +myUserId &&
        +trade.sellOrderId === +order.id

      if (!isMyBidFill && !isMyAskFill) return [order]

      return +trade.quantity >= +order.shares
        ? []
        : [{ ...order, shares: +minus(order.shares, trade.quantity), remainingShares: +minus(order.shares, trade.quantity) }]
    })
  } else if (type === USER_ACTIVITY_TYPES.ORDER_RESTED) {
    const restedOrder = log?.order
    if (
      !restedOrder ||
      restedOrder.orderType === ORDER_TYPES.MARKET ||
      myUserId == null ||
      +activityUserId !== +myUserId
    ) {
      return updatedMyLimitOrders
    }

    const shares = +(restedOrder.remainingShares ?? 0)
    if (shares <= 0) {
      return updatedMyLimitOrders.filter(order => +order.id !== +restedOrder.id)
    }

    const index = updatedMyLimitOrders.findIndex(order => +order.id === +restedOrder.id)
    const normalizedOrder = { ...restedOrder, shares, remainingShares: shares, userId: myUserId }

    if (index !== -1) {
      updatedMyLimitOrders = updatedMyLimitOrders.map((order, i) =>
        i === index ? { ...order, ...normalizedOrder } : order
      )
    } else {
      updatedMyLimitOrders = [...updatedMyLimitOrders, normalizedOrder]
    }
  } else if (type === USER_ACTIVITY_TYPES.ORDER_PLACED) {
    const placedOrder = log?.order
    // Market feed is shared — only ingest when activity belongs to the user of our tracked orders
    if (
      !placedOrder ||
      placedOrder.orderType === ORDER_TYPES.MARKET ||
      myUserId == null ||
      +activityUserId !== +myUserId
    ) {
      return updatedMyLimitOrders
    }

    const shares = +(placedOrder.remainingShares ?? placedOrder.shares ?? 0)
    if (shares <= 0) return updatedMyLimitOrders

    const normalizedOrder = { ...placedOrder, shares, userId: myUserId }

    const index = updatedMyLimitOrders.findIndex(order =>
      +order.userId === +normalizedOrder.userId &&
      +order.optionId === +normalizedOrder.optionId &&
      order.outcome === normalizedOrder.outcome &&
      order.side === normalizedOrder.side &&
      +order.price === +normalizedOrder.price
    )

    if (index !== -1) {
      updatedMyLimitOrders = updatedMyLimitOrders.map((order, i) =>
        i === index
          ? { ...order, shares: +plus(order.shares, shares) }
          : order
      )
    } else {
      updatedMyLimitOrders = [...updatedMyLimitOrders, normalizedOrder]
    }
  } else if (type === USER_ACTIVITY_TYPES.ORDER_CLOSED) {
    const closedOrder = log?.order
    if (
      !closedOrder ||
      closedOrder.orderType === ORDER_TYPES.MARKET ||
      myUserId == null ||
      +activityUserId !== +myUserId
    ) {
      return updatedMyLimitOrders
    }

    const closedShares = +(closedOrder.remainingShares ?? closedOrder.shares ?? 0)

    updatedMyLimitOrders = updatedMyLimitOrders.flatMap(order => {
      if (
        +order.userId === +closedOrder.userId &&
        +order.optionId === +closedOrder.optionId &&
        order.outcome === closedOrder.outcome &&
        order.side === closedOrder.side &&
        +order.price === +closedOrder.price
      ) {
        const remainingShares = +minus(order.shares, closedShares)
        return remainingShares <= 0
          ? []
          : [{ ...order, shares: remainingShares }]
      }

      return order
    })
  }
  return updatedMyLimitOrders
}

const NOTIFICATION_ICONS = {
  [NOTIFICATION_CATEGORIES.ORDER]: 'fa-clipboard-list',
  [NOTIFICATION_CATEGORIES.TRADE]: 'fa-arrow-right-arrow-left',
  [NOTIFICATION_CATEGORIES.ORDER_COMPLETE]: 'fa-circle-check',
  [NOTIFICATION_CATEGORIES.POSITION]: 'fa-arrows-split-up-and-left',
  [NOTIFICATION_CATEGORIES.RESOLUTION]: 'fa-gavel',
  [NOTIFICATION_CATEGORIES.WALLET]: 'fa-wallet'
}

const MAX_NOTIFICATIONS = 50

export function getNotificationCategory (type) {
  switch (type) {
    case USER_ACTIVITY_TYPES.ORDER_PLACED:
      return NOTIFICATION_CATEGORIES.ORDER
    case USER_ACTIVITY_TYPES.BOUGHT_SHARES:
    case USER_ACTIVITY_TYPES.SOLD_SHARES:
      return NOTIFICATION_CATEGORIES.TRADE
    case USER_ACTIVITY_TYPES.ORDER_CLOSED:
      return NOTIFICATION_CATEGORIES.ORDER_COMPLETE
    case USER_ACTIVITY_TYPES.SPLIT:
    case USER_ACTIVITY_TYPES.MERGE:
      return NOTIFICATION_CATEGORIES.POSITION
    case USER_ACTIVITY_TYPES.POSITION_RESOLVED:
    case 'market_resolved':
      return NOTIFICATION_CATEGORIES.RESOLUTION
    case 'wallet_balance_updated':
      return NOTIFICATION_CATEGORIES.WALLET
    default:
      return NOTIFICATION_CATEGORIES.ORDER
  }
}

export function getNotificationIcon (category) {
  return NOTIFICATION_ICONS[category] ?? 'fa-bell'
}

export function mapActivityToNotification (activity, { read = false } = {}) {
  if (!activity) return null

  const mapped = mapActivityLogRow(activity)
  const { type, log } = activity
  const category = getNotificationCategory(type)

  const questionTitle =
    mapped.questionTitle ||
    formatStoredCryptoQuestionTitle(log?.questionTitle, log) ||
    getTranslation('activityUpdate')

  let message = mapped.description || ''
  if (!message && type === USER_ACTIVITY_TYPES.POSITION_RESOLVED) {
    message = getTranslation('marketQuestionResolved', {
      questionTitle: questionTitle || getTranslation('yourPositionText')
    })
  }

  return {
    id: String(activity.id ?? `${type}-${activity.createdAt ?? Date.now()}`),
    type,
    category,
    title: questionTitle,
    optionTitle: mapped.optionTitle || log?.optionTitle,
    message,
    questionId: activity.questionId ?? log?.questionId,
    optionId: activity.optionId ?? log?.optionId,
    thumbnailUrl: mapped.thumbnailUrl || log?.thumbnailUrl,
    createdAt: activity.createdAt ?? new Date().toISOString(),
    read
  }
}

export function mapWalletToNotification ({ balance, currency, type }) {
  const formattedBalance = Number(balance ?? 0).toFixed(2)
  const currencyLabel = currency ?? ''

  let message
  if (type === 'credit') {
    message = getTranslation('yourBalanceWasCredited', {
      currency: currencyLabel,
      formattedBalance
    })
  } else if (type === 'resolve') {
    message = getTranslation('marketResolutionPayoutReceived', {
      currency: currencyLabel,
      formattedBalance
    })
  } else {
    message = getTranslation('yourBalanceWasDebited', {
      currency: currencyLabel,
      formattedBalance
    })
  }

  return {
    id: `wallet-${Date.now()}`,
    type: 'wallet_balance_updated',
    category: NOTIFICATION_CATEGORIES.WALLET,
    title: type === 'resolve' ? getTranslation('marketResolved') : getTranslation('walletUpdated'),
    optionTitle: null,
    message,
    questionId: null,
    optionId: null,
    thumbnailUrl: null,
    createdAt: new Date().toISOString(),
    read: false
  }
}

export function formatNotificationTime (value) {
  const date = new Date(value)
  if (Number.isNaN(date.getTime())) return ''

  const diffMs = Date.now() - date.getTime()
  const diffMinutes = Math.floor(diffMs / 60000)

  if (diffMinutes < 1) return getTranslation('justNow')
  if (diffMinutes < 60) return getTranslation('minutesAgo', { minutes: diffMinutes })

  const diffHours = Math.floor(diffMinutes / 60)
  if (diffHours < 24) return getTranslation('hoursAgo', { hours: diffHours })

  const diffDays = Math.floor(diffHours / 24)
  if (diffDays < 7) return getTranslation('daysAgo', { days: diffDays })

  return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })
}

export function prependNotification (items, notification) {
  if (!notification) return items

  const exists = items.some((item) => item.id === notification.id)
  if (exists) return items

  return [notification, ...items].slice(0, MAX_NOTIFICATIONS)
}